import React, { useEffect, useState } from 'react';
import { Button, FadeIn, Logo } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api/client';

const Dots = ({ active }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '8px 0 18px' }}>
    {[0, 1, 2].map((i) => (
      <span key={i} style={{
        width: 26, height: 5, borderRadius: 999,
        background: i <= active ? 'var(--primary)' : 'var(--border)',
      }} />
    ))}
  </div>
);

// Where + what a newly approved staff member will serve, then experience.
export default function StaffSetup() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState('congrats'); // congrats | locations | categories | experience
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selLoc, setSelLoc] = useState([]);
  const [selCat, setSelCat] = useState([]);
  const [experience, setExperience] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/catalog/locations').then((d) => setLocations(d?.locations || [])).catch(() => {});
    api.get('/catalog/categories').then((d) => setCategories(d?.categories || [])).catch(() => {});
  }, []);

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const finish = async () => {
    setLoading(true);
    try {
      await api.put('/users/me', {
        serviceAreas: selLoc,
        skills: selCat,
        experienceYears: Number(experience) || 0,
      });
      await refreshUser();
    } catch (err) {
      toast(err.message || 'Please try again', { title: 'Could not save', tone: 'error' });
    }
    setLoading(false);
  };

  const shell = (children, center) => (
    <div className="app-shell">
      <div className="screen screen--plain" style={{
        padding: 22, paddingTop: 'calc(var(--sat) + 22px)', maxWidth: 560, margin: '0 auto', width: '100%',
        ...(center ? { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' } : {}),
      }}>
        {children}
      </div>
    </div>
  );

  // ---- Congrats ----
  if (step === 'congrats') {
    return shell(
      <FadeIn style={{ textAlign: 'center' }}>
        <div style={{
          width: 110, height: 110, borderRadius: '50%', background: '#E5F5EE', margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 54,
        }}>🎉</div>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginTop: 20 }}>
          Congratulations{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
        </h1>
        <p className="muted" style={{ fontSize: 14.5, marginTop: 10, lineHeight: 1.5 }}>
          Your account has been approved and verified. Let's set up where and what you'll serve.
        </p>
        <Button title="Next  →" onClick={() => setStep('locations')}
          style={{ marginTop: 30, maxWidth: 220, marginLeft: 'auto', marginRight: 'auto' }} />
      </FadeIn>,
      true
    );
  }

  // ---- Experience ----
  if (step === 'experience') {
    return shell(
      <>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Logo size="sm" /></div>
        <Dots active={2} />
        <h1 style={{ fontSize: 22, fontWeight: 800, textAlign: 'center' }}>Your work experience</h1>
        <p className="muted center" style={{ marginBottom: 22 }}>How many years of experience do you have?</p>

        <div className="row" style={{ justifyContent: 'center', gap: 14, marginTop: 20 }}>
          <input
            value={experience} inputMode="numeric" maxLength={2} placeholder="0"
            onChange={(e) => setExperience(e.target.value.replace(/\D/g, '').slice(0, 2))}
            style={{
              width: 110, textAlign: 'center', fontSize: 40, fontWeight: 800, color: 'var(--primary)',
              border: 'none', borderBottom: '3px solid var(--primary)', outline: 'none',
              padding: '6px 0', background: 'transparent',
            }}
          />
          <span className="muted" style={{ fontSize: 18, fontWeight: 700 }}>years</span>
        </div>

        <Button title={loading ? 'Saving…' : 'Finish Setup'} onClick={finish} disabled={loading} style={{ marginTop: 26 }} />
        <Button variant="ghost" title="← Back" onClick={() => setStep('categories')} style={{ marginTop: 10 }} />
      </>
    );
  }

  const isLoc = step === 'locations';
  const list = isLoc ? locations : categories;
  const sel = isLoc ? selLoc : selCat;

  return shell(
    <>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Logo size="sm" /></div>
      <Dots active={isLoc ? 0 : 1} />

      <h1 style={{ fontSize: 22, fontWeight: 800, textAlign: 'center' }}>
        {isLoc ? 'Where do you provide service?' : 'What services do you offer?'}
      </h1>
      <p className="muted center" style={{ marginBottom: 22, lineHeight: 1.5 }}>
        {isLoc ? 'Select all locations you can work in (multiple).' : 'Select all categories you provide (multiple).'}
      </p>

      {list.length === 0 ? (
        <p className="muted center" style={{ marginTop: 20 }}>
          No {isLoc ? 'locations' : 'categories'} available yet. Please contact admin.
        </p>
      ) : (
        <div className="chip-wrap" style={{ justifyContent: 'center' }}>
          {list.map((item) => {
            const val = item.name;
            const on = sel.includes(val);
            return (
              <button
                key={item._id}
                className={`chip ${on ? 'chip--soft-on' : ''}`}
                onClick={() => (isLoc ? toggle(selLoc, setSelLoc, val) : toggle(selCat, setSelCat, val))}
              >
                <span>{isLoc ? '📍' : (item.icon || '🧰')}</span>
                <span>{isLoc ? [item.name, item.city].filter(Boolean).join(', ') : item.name}</span>
                {on && <span style={{ fontWeight: 800 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}

      {isLoc ? (
        <Button
          title="Next  →" style={{ marginTop: 26 }} disabled={!selLoc.length}
          onClick={() => (selLoc.length ? setStep('categories') : toast('Select at least one location', { tone: 'error' }))}
        />
      ) : (
        <>
          <Button
            title="Next  →" style={{ marginTop: 26 }} disabled={!selCat.length}
            onClick={() => (selCat.length ? setStep('experience') : toast('Select at least one service category', { tone: 'error' }))}
          />
          <Button variant="ghost" title="← Back" onClick={() => setStep('locations')} style={{ marginTop: 10 }} />
        </>
      )}
    </>
  );
}
